/**
 * The KickBack mark.
 *
 * Inline SVG rather than an `<img>` or an icon font, for the same reason
 * `styles.css` is plain CSS and not a CDN framework: a logo that is fetched is
 * a logo that can fail to arrive, or be changed by whoever serves it. This one
 * is part of the bundle and renders before the first paint.
 *
 * The geometry is a 100x100 grid: a circular arc that starts in the accent blue
 * and hands off to the "money arrived" green, closing on an arrowhead pointing
 * back at the K. Commission goes out and comes back — the mark is the circuit,
 * not a monogram.
 *
 * Two variants, because the surface differs:
 *
 *   `bare`  — no tile, the K takes `currentColor`. Reads correctly on both the
 *             light and dark themes, which is why every in-app use is this one.
 *   `tile`  — navy rounded tile with a white K, for a favicon or anywhere the
 *             mark sits on a surface we do not control.
 *
 * `tile` is deliberately not used inside the app: its navy is a near-match for
 * the dark theme's `--surface`, so the tile dissolves into the card it sits on.
 * It earns its place where the background is unknown, and nowhere else.
 */

/** Arc endpoints, precomputed from r=35.5 about (50,50). See the module note. */
const ARC_BLUE = 'M 28.14 22.03 A 35.5 35.5 0 0 0 37.86 83.36';
const ARC_GREEN = 'M 36.13 82.68 A 35.5 35.5 0 0 0 85.15 45.06';
const ARROWHEAD = '83.27,31.69 93.27,43.92 77.03,46.20';

/**
 * The K as an outline rather than a `<text>` element.
 *
 * Two reasons, both of which bit the first version. A glyph renders differently
 * wherever the font is missing, which for a logo is the one thing that must not
 * vary. And an SVG `<text>` node joins the page's text content, so selecting
 * the header copied "KKickBack" and the mark's letter came along with it.
 */
const K_OUTLINE =
  '34,34 43.5,34 43.5,47.5 55.5,34 67,34 52.8,49.5 67.5,67 55.8,67 43.5,53.5 43.5,67 34,67';

export function Logo({
  size = 24,
  variant = 'bare',
  title,
}: {
  size?: number;
  variant?: 'bare' | 'tile';
  /**
   * Only pass this when the mark stands alone. Beside the "KickBack" wordmark
   * it is decorative, and naming it there makes a screen reader say the brand
   * twice.
   */
  title?: string | undefined;
}) {
  const tiled = variant === 'tile';

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role={title === undefined ? 'presentation' : 'img'}
      aria-hidden={title === undefined ? true : undefined}
      aria-label={title}
      focusable="false"
    >
      {tiled && <rect width="100" height="100" rx="26" ry="26" fill="#16203a" />}

      <path d={ARC_BLUE} stroke="#5b8cff" strokeWidth="7.4" strokeLinecap="round" />
      <path d={ARC_GREEN} stroke="#34d399" strokeWidth="7.4" strokeLinecap="round" />
      <polygon points={ARROWHEAD} fill="#34d399" />

      {/* `currentColor` is the whole point of the bare variant: the K follows
          the surrounding text through a theme change without a second asset. */}
      <polygon points={K_OUTLINE} fill={tiled ? '#ffffff' : 'currentColor'} />
    </svg>
  );
}

/** Mark plus wordmark. The header's brand block, and nothing else so far. */
export function Wordmark({ size = 22 }: { size?: number }) {
  return (
    <span className="wordmark">
      <Logo size={size} />
      <span className="wordmark__text">
        Kick<span className="wordmark__back">Back</span>
      </span>
    </span>
  );
}
